import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../config';
import './databases.css';

const CATEGORIES = ['Plumbing', 'Electrical', 'HVAC', 'Landscaping', 'Cleaning', 'Roofing', 'Painting', 'General Contractor', 'Legal', 'Accounting', 'Insurance', 'Supplies', 'Other'];
const STATUSES = ['Active', 'Inactive', 'Preferred', 'Blacklisted'];

function VendorsCard() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [form, setForm] = useState({ name: '', category: '', contact_name: '', email: '', phone: '', address: '', website: '', tax_id: '', payment_terms: '', notes: '', status: 'Active' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Column settings state
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [dbColumns, setDbColumns] = useState([]);

  // CSV upload state
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  // Attribute management state
  const [showAddAttributeModal, setShowAddAttributeModal] = useState(false);
  const [showDeleteAttributeModal, setShowDeleteAttributeModal] = useState(false);
  const [newAttribute, setNewAttribute] = useState('');
  const [newAttributeType, setNewAttributeType] = useState('string');
  const [newAttributeRelationTable, setNewAttributeRelationTable] = useState('');
  const [attributeToDelete, setAttributeToDelete] = useState('');
  const [customAttributes, setCustomAttributes] = useState([]);
  const [attributeTypes, setAttributeTypes] = useState({});
  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [relationOptions, setRelationOptions] = useState({});

  useEffect(() => { fetchVendors(); fetchDbColumns(); fetchAttributes(); fetchAttributeTypes(); fetchAvailableDatabases(); }, []);

  const fetchVendors = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/vendors`);
      if (!res.ok) throw new Error('Failed to fetch vendors');
      setVendors(await res.json());
    } catch (err) { setError(err.message); setVendors([]); }
    finally { setLoading(false); }
  };

  const fetchDbColumns = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/vendors/columns`);
      if (!res.ok) return;
      const data = await res.json();
      setDbColumns(Array.isArray(data.columns) ? data.columns : []);
    } catch (e) { console.error('Error fetching DB columns:', e); }
  };

  const fetchAttributes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/vendors/attributes`);
      if (!res.ok) return;
      const data = await res.json();
      setCustomAttributes(Array.isArray(data.attributes) ? data.attributes : []);
    } catch (e) { console.error('Error fetching attributes:', e); }
  };

  const fetchAttributeTypes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/vendors/attribute-types`);
      if (!res.ok) return;
      const data = await res.json();
      const types = data.attribute_types || {};
      setAttributeTypes(types);
      Object.entries(types).forEach(([, typeVal]) => {
        if (typeof typeVal === 'string' && typeVal.startsWith('relation:')) fetchRelationOptions(typeVal.split(':')[1]);
      });
    } catch (e) { console.error('Error fetching attribute types:', e); }
  };

  const fetchAvailableDatabases = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/available-databases`);
      if (!res.ok) return;
      const data = await res.json();
      setAvailableDatabases(data.databases || []);
    } catch (e) { console.error('Error fetching available databases:', e); }
  };

  const fetchRelationOptions = async (tableName) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/${tableName}/relation-options`);
      if (!res.ok) return;
      const data = await res.json();
      setRelationOptions(prev => ({ ...prev, [tableName]: data.options || [] }));
    } catch (e) { console.error('Error fetching relation options:', e); }
  };

  const addAttributeHandler = async () => {
    if (!newAttribute.trim()) return;
    setLoading(true);
    try {
      const payload = { attribute: newAttribute, type: newAttributeType };
      if (newAttributeType === 'relation') payload.relation_table = newAttributeRelationTable;
      const res = await fetch(`${API_BASE_URL}/api/vendors/attributes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed to add attribute'); }
      await fetchVendors(); await fetchDbColumns(); await fetchAttributes(); await fetchAttributeTypes();
      setShowAddAttributeModal(false); setNewAttribute(''); setNewAttributeType('string'); setNewAttributeRelationTable('');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const deleteAttributeHandler = async () => {
    if (!attributeToDelete.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/vendors/attributes/${attributeToDelete}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete attribute');
      await fetchVendors(); await fetchDbColumns(); await fetchAttributes();
      setShowDeleteAttributeModal(false); setAttributeToDelete('');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Vendor name is required'); return; }
    setLoading(true); setError('');
    try {
      const url = editVendor ? `${API_BASE_URL}/api/vendors/${editVendor.id}` : `${API_BASE_URL}/api/vendors`;
      const res = await fetch(url, { method: editVendor ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      closeModal();
      await fetchVendors();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    setLoading(true);
    try { await fetch(`${API_BASE_URL}/api/vendors/${id}`, { method: 'DELETE' }); await fetchVendors(); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setEditVendor(null);
    const base = { name: '', category: '', contact_name: '', email: '', phone: '', address: '', website: '', tax_id: '', payment_terms: '', notes: '', status: 'Active' };
    customAttributes.forEach(attr => { base[attr.replace(/\s+/g, '_').replace(/-/g, '_').toLowerCase()] = ''; });
    setForm(base);
    Object.entries(attributeTypes).forEach(([, typeVal]) => { if (typeof typeVal === 'string' && typeVal.startsWith('relation:')) fetchRelationOptions(typeVal.split(':')[1]); });
    setShowModal(true);
  };
  const openEdit = (v) => {
    setEditVendor(v);
    const base = { name: v.name||'', category: v.category||'', contact_name: v.contact_name||'', email: v.email||'', phone: v.phone||'', address: v.address||'', website: v.website||'', tax_id: v.tax_id||'', payment_terms: v.payment_terms||'', notes: v.notes||'', status: v.status||'Active' };
    customAttributes.forEach(attr => { const k = attr.replace(/\s+/g, '_').replace(/-/g, '_').toLowerCase(); base[k] = v[k] || ''; });
    setForm(base);
    Object.entries(attributeTypes).forEach(([, typeVal]) => { if (typeof typeVal === 'string' && typeVal.startsWith('relation:')) fetchRelationOptions(typeVal.split(':')[1]); });
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditVendor(null); };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const handleCSVUpload = async () => {
    if (!uploadFile) { setError('Please select a file to upload'); return; }
    setLoading(true); setUploadProgress('Uploading...');
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      const response = await fetch(`${API_BASE_URL}/api/vendors/upload-csv`, { method: 'POST', body: formData, signal: controller.signal });
      clearTimeout(timeoutId);
      const result = await response.json();
      if (response.ok) {
        setUploadProgress(`Upload completed! ${result.success_count} records imported successfully.`);
        if (result.error_count > 0) setUploadProgress(prev => prev + ` ${result.error_count} errors occurred.`);
        await fetchVendors();
        setTimeout(() => { setShowExcelUploadModal(false); setUploadFile(null); setUploadProgress(null); }, 2000);
      } else {
        setUploadProgress(`Error: ${result.error}`);
      }
    } catch (err) { setUploadProgress(`Upload failed: ${err.message}`); }
    finally { setLoading(false); }
  };

  const toggleColumnVisibility = (col) => {
    setHiddenColumns(prev => prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]);
  };

  const filtered = vendors.filter(v =>
    [v.name, v.category, v.contact_name, v.email, v.phone, v.status].some(val => val && val.toString().toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const sorted = [...filtered].sort((a, b) => {
    const va = a[sortField] ?? '', vb = b[sortField] ?? '';
    const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const startIdx = (currentPage - 1) * rowsPerPage;
  const paginated = sorted.slice(startIdx, startIdx + rowsPerPage);

  const statusColor = (s) => {
    switch(s) {
      case 'Active': return { background: '#dcfce7', color: '#166534' };
      case 'Preferred': return { background: '#dbeafe', color: '#1e40af' };
      case 'Inactive': return { background: '#f3f4f6', color: '#6b7280' };
      case 'Blacklisted': return { background: '#fef2f2', color: '#991b1b' };
      default: return { background: '#f3f4f6', color: '#6b7280' };
    }
  };

  const COLUMNS = [
    { key: 'name', label: 'Name' },
    { key: 'category', label: 'Category' },
    { key: 'contact_name', label: 'Contact' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'status', label: 'Status' },
  ];

  const visibleColumns = COLUMNS.filter(col => !hiddenColumns.includes(col.key));

  return (
    <div className="notion-card">
      <div className="notion-header">
        <h1 className="notion-title">Vendors</h1>
        <div className="notion-actions">
          <input type="text" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} placeholder="🔍 Search Vendors" className="search" />
          <button className="notion-button" onClick={() => handleSort(sortField)}>Sort {sortDir === 'asc' ? '↑' : '↓'}</button>
          <button className="notion-button notion-button-primary" onClick={openAdd} disabled={loading}>+ Add Vendor</button>
          <button onClick={() => setShowAddAttributeModal(true)} className="notion-button" disabled={loading}>+ Add Attribute</button>
          <button onClick={() => setShowDeleteAttributeModal(true)} className="notion-button" disabled={loading || customAttributes.length === 0}>- Delete Attribute</button>
          <button onClick={() => setShowExcelUploadModal(true)} className="notion-button" disabled={loading}>📊 Upload CSV</button>
          <button onClick={() => setShowColumnSettings(true)} className="notion-button">⚙️ Column Settings</button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {loading && <div className="loading">Loading vendors...</div>}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="notion-modal-title">{editVendor ? 'Edit Vendor' : 'Add Vendor'}</h2>
            <div className="notion-form">
              <div className="form-row"><label>Name *</label><input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Company name" /></div>
              <div className="form-row"><label>Category</label>
                <select value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))}>
                  <option value="">Select...</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-row"><label>Contact Name</label><input value={form.contact_name} onChange={e => setForm(p => ({...p, contact_name: e.target.value}))} placeholder="Primary contact" /></div>
              <div className="form-row"><label>Email</label><input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="vendor@example.com" /></div>
              <div className="form-row"><label>Phone</label><input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="(555) 555-5555" /></div>
              <div className="form-row"><label>Address</label><input value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} placeholder="Street, City, State" /></div>
              <div className="form-row"><label>Website</label><input value={form.website} onChange={e => setForm(p => ({...p, website: e.target.value}))} placeholder="https://..." /></div>
              <div className="form-row"><label>Tax ID</label><input value={form.tax_id} onChange={e => setForm(p => ({...p, tax_id: e.target.value}))} placeholder="EIN / Tax ID" /></div>
              <div className="form-row"><label>Payment Terms</label><input value={form.payment_terms} onChange={e => setForm(p => ({...p, payment_terms: e.target.value}))} placeholder="Net 30, COD, etc." /></div>
              <div className="form-row"><label>Status</label>
                <select value={form.status} onChange={e => setForm(p => ({...p, status: e.target.value}))}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-row"><label>Notes</label><textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={3} placeholder="Additional notes..." /></div>
              {customAttributes.map(attr => {
                const dbKey = attr.replace(/\s+/g, '_').replace(/-/g, '_').toLowerCase();
                const typeVal = attributeTypes[attr] || 'string';
                const isRelation = typeVal.startsWith('relation:');
                const relationTable = isRelation ? typeVal.split(':')[1] : null;
                const options = isRelation ? (relationOptions[relationTable] || []) : [];
                return (
                  <div key={attr} className="form-row">
                    <label>{attr}</label>
                    {isRelation ? (
                      <select value={form[dbKey] || ''} onChange={e => setForm(p => ({...p, [dbKey]: e.target.value}))}>
                        <option value="">Select...</option>
                        {options.map(opt => <option key={opt.id} value={opt.name}>{opt.name}</option>)}
                      </select>
                    ) : (
                      <input type={typeVal === 'number' || typeVal === 'currency' ? 'number' : 'text'} step={typeVal === 'currency' ? '0.01' : 'any'} value={form[dbKey] || ''} onChange={e => setForm(p => ({...p, [dbKey]: e.target.value}))} placeholder={attr} />
                    )}
                  </div>
                );
              })}
              <div className="form-actions">
                <button className="notion-button notion-button-primary" onClick={handleSave} disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
                <button className="notion-button" onClick={closeModal}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showExcelUploadModal && (
        <div className="modal-overlay" onClick={() => { setShowExcelUploadModal(false); setUploadFile(null); setUploadProgress(null); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="notion-modal-title">Upload CSV File</h2>
            <div className="excel-upload-info">
              <p><strong>Instructions:</strong></p>
              <ul>
                <li>Upload a CSV file (.csv) with vendor data</li>
                <li>Column headers should match: name, category, contact_name, email, phone, address, website, tax_id, payment_terms, notes, status</li>
                <li>Any additional columns will be added as new attributes</li>
              </ul>
            </div>
            <div className="notion-modal-field">
              <label>Select CSV File</label>
              <input type="file" accept=".csv" onChange={(e) => setUploadFile(e.target.files[0])} className="notion-input" />
            </div>
            {uploadProgress && <div className="upload-progress">{uploadProgress}</div>}
            <div className="form-actions">
              <button className="notion-button notion-button-primary" onClick={handleCSVUpload} disabled={loading || !uploadFile}>{loading ? 'Uploading...' : 'Upload'}</button>
              <button className="notion-button" onClick={() => { setShowExcelUploadModal(false); setUploadFile(null); setUploadProgress(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showColumnSettings && (
        <div className="modal-overlay" onClick={() => setShowColumnSettings(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="notion-modal-title">Column Settings</h2>
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Column Visibility</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {COLUMNS.map(col => (
                  <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" checked={!hiddenColumns.includes(col.key)} onChange={() => toggleColumnVisibility(col.key)} />
                    {col.label}
                  </label>
                ))}
              </div>
            </div>
            {dbColumns.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Available DB Columns</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                  {dbColumns.filter(col => !['id'].includes(col)).map(col => {
                    const title = col.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
                    return <span key={col} style={{ fontSize: '0.85rem', color: '#6b7280' }}>{title}</span>;
                  })}
                </div>
              </div>
            )}
            <div className="form-actions">
              <button className="notion-button" onClick={() => setShowColumnSettings(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showAddAttributeModal && (
        <div className="modal-overlay" onClick={() => setShowAddAttributeModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="notion-modal-title">Add New Attribute</h2>
            <div className="notion-form">
              <div className="form-row"><label>Attribute Name</label><input type="text" value={newAttribute} onChange={e => setNewAttribute(e.target.value)} placeholder="Enter attribute name" /></div>
              <div className="form-row"><label>Data Type</label>
                <select value={newAttributeType} onChange={e => { setNewAttributeType(e.target.value); if (e.target.value !== 'relation') setNewAttributeRelationTable(''); }}>
                  <option value="string">Text (String)</option>
                  <option value="number">Number</option>
                  <option value="currency">Currency</option>
                  <option value="relation">Relation (Link to Database)</option>
                </select>
              </div>
              {newAttributeType === 'relation' && (
                <div className="form-row"><label>Link to Database</label>
                  <select value={newAttributeRelationTable} onChange={e => setNewAttributeRelationTable(e.target.value)}>
                    <option value="">Select a database...</option>
                    {availableDatabases.filter(db => db.table_name !== 'vendors').map(db => (
                      <option key={db.table_name} value={db.table_name}>{db.icon} {db.display_name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-actions">
                <button className="notion-button notion-button-primary" onClick={addAttributeHandler} disabled={loading || !newAttribute.trim() || (newAttributeType === 'relation' && !newAttributeRelationTable)}>Add Attribute</button>
                <button className="notion-button" onClick={() => setShowAddAttributeModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteAttributeModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteAttributeModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="notion-modal-title">Delete Attribute</h2>
            <div className="notion-form">
              <div className="form-row"><label>Select Attribute to Delete</label>
                <select value={attributeToDelete} onChange={e => setAttributeToDelete(e.target.value)}>
                  <option value="">Select an attribute</option>
                  {customAttributes.map(attr => <option key={attr} value={attr}>{attr}</option>)}
                </select>
              </div>
              <div className="form-actions">
                <button className="notion-button notion-button-danger" onClick={deleteAttributeHandler} disabled={loading || !attributeToDelete}>Delete Attribute</button>
                <button className="notion-button" onClick={() => setShowDeleteAttributeModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="notion-table-container">
        <table className="notion-table">
          <thead>
            <tr>
              {visibleColumns.map(col => (
                <th key={col.key} className="notion-th" style={{ cursor: 'pointer' }} onClick={() => handleSort(col.key)}>
                  {col.label} {sortField === col.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
              ))}
              <th className="notion-th" style={{ width: '100px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map(v => (
              <tr key={v.id} className="notion-row">
                {visibleColumns.map(col => {
                  if (col.key === 'status') {
                    return (
                      <td key={col.key} className="notion-td">
                        {v.status && <span style={{ ...statusColor(v.status), padding: '2px 8px', borderRadius: 10, fontSize: '0.6875rem', fontWeight: 600 }}>{v.status}</span>}
                      </td>
                    );
                  }
                  return <td key={col.key} className="notion-td" style={col.key === 'name' ? { fontWeight: 500 } : {}}>{v[col.key] || '—'}</td>;
                })}
                <td className="notion-td">
                  <button onClick={() => openEdit(v)} className="notion-action" disabled={loading}>Edit</button>
                  <button onClick={() => handleDelete(v.id)} className="notion-action" style={{ marginLeft: 6, color: '#dc3545' }} disabled={loading}>Del</button>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && !loading && (
              <tr>
                <td colSpan={visibleColumns.length + 1} className="notion-td" style={{ textAlign: 'center', padding: 0, border: 'none' }}>
                  <div className="db-empty-state" style={{ border: 'none', borderRadius: 0 }}>
                    <div className="db-empty-state-icon">🏪</div>
                    <div className="db-empty-state-title">No vendors yet</div>
                    <div className="db-empty-state-text">Add contractors, suppliers, and service providers to your vendor directory.</div>
                    <button className="db-empty-state-btn" onClick={openAdd}>+ Add Vendor</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      <div className="db-pagination">
        <div className="db-pagination-left">
          <label>Rows per page:</label>
          <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
            <option value={5}>5</option><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
          </select>
        </div>
        <div className="db-pagination-center">Showing {sorted.length === 0 ? 0 : startIdx + 1} to {Math.min(startIdx + rowsPerPage, sorted.length)} of {sorted.length} vendors</div>
        <div className="db-pagination-right">
          <button className="db-pagination-btn" onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage === 1}>Previous</button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pn; if (totalPages<=5) pn=i+1; else if(currentPage<=3) pn=i+1; else if(currentPage>=totalPages-2) pn=totalPages-4+i; else pn=currentPage-2+i;
            return <button key={pn} className={`db-pagination-btn ${currentPage===pn?'active':''}`} onClick={() => setCurrentPage(pn)}>{pn}</button>;
          })}
          <button className="db-pagination-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage>=totalPages}>Next</button>
        </div>
      </div>
      </div>
    </div>
  );
}

export default VendorsCard;
